<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="deadlineEntriesWithStatus"
      no-data-text="Keine Fristeneinträge"
      sort-by="dueDate"
      :group-by.sync="groupBySelector.value"
      :search="search"
    >
      <template v-slot:top>
        <slot name="top">
          <v-card flat>
            <v-card-title v-if="!hideTitle" class="body-2 v-card__subtitle"
              ><v-icon left small class="mr-2">mdi-timer-sand</v-icon
              >{{ title }}
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-subtitle>
              <v-row>
                <v-col cols="12" sm="5" md="3">
                  <v-text-field
                    v-model="search"
                    outlined
                    append-icon="mdi-magnify"
                    label="Suchen"
                    hide-details="auto"
                    clearable
                    dense
                  ></v-text-field>
                </v-col>
                <v-col v-if="allowGrouping" class="d-flex justify-end">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        outlined
                        text
                        v-bind="attrs"
                        v-on="on"
                        class="mr-2"
                      >
                        <span v-if="groupBySelector.value == null"
                          >Gruppieren</span
                        >
                        <span v-else>
                          Gruppieren nach
                          <strong class="hidden-sm-and-down">{{
                            groupBySelector.text
                          }}</strong>
                          <v-icon small class="hidden-md-and-up">{{
                            groupBySelector.icon
                          }}</v-icon>
                        </span>
                        <v-icon right>mdi-menu-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list nav>
                      <v-list-item-group v-model="groupBySelector" mandatory>
                        <v-list-item
                          link
                          dense
                          :value="{
                            value: null,
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-close</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title
                            >Gruppierung aufheben</v-list-item-title
                          >
                        </v-list-item>
                        <v-list-item
                          v-if="allowGroupingByStatus"
                          link
                          dense
                          :value="{
                            text: 'Status',
                            value: 'groupByStatus',
                            icon: 'mdi-clock',
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-list-status</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Status</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          v-if="allowGroupingByDeadline"
                          link
                          dense
                          :value="{
                            text: 'Frist',
                            value: 'groupByDeadline',
                            icon: 'mdi-clock',
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-clock</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Frist</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          v-if="allowGroupingByPerson"
                          link
                          dense
                          :value="{
                            text: 'Person',
                            value: 'groupByPerson',
                            icon: 'mdi-account',
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-account</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Person</v-list-item-title>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-menu>

                  <v-btn
                    v-if="allowExport"
                    outlined
                    text
                    @click="exportData"
                    disabled
                  >
                    <v-icon left>mdi-file-export</v-icon>
                    Exportieren
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-subtitle>
          </v-card>
        </slot>
      </template>
      <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
        <th :colspan="headers.length + 1">
          <v-icon @click="toggle" class="mr-2"
            >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>

          <span v-if="groupBySelector.value == 'groupByPerson'"
            >{{ items[0].user.displayName }}
          </span>
          <span v-else-if="groupBySelector.value == 'groupByDeadline'">{{
            items[0].deadline.title
          }}</span>
          ({{ items.length }})
        </th>
      </template>
      <template v-slot:[`item.labels`]="{ item }">
        <v-chip
          small
          outlined
          v-for="(label, index) in item.labels"
          :key="index"
          class="py-0 mb-0 mt-1 mr-1"
        >
          {{ label }}</v-chip
        >
      </template>
      <template v-slot:[`item.personnelNumber`]="{ item }">
        <pre>{{ item.user.personnelNumber }}</pre>
      </template>
      <template v-slot:[`item.dueDate`]="{ item }">
        <pre v-if="item.dueDate">{{ timestampConverter(item.dueDate) }}</pre>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <deadline-entry-status-chip
          :status="item.status"
          small
        ></deadline-entry-status-chip>
      </template>
      <template v-slot:[`item.scheduledRenewal`]="{ item }">
        <span v-if="item.scheduledRenewal">
          <strong>{{
            timestampConverterWithTime(item.scheduledRenewal.timestamp)
          }}</strong
          ><br />
          {{ item.scheduledRenewal.location }}, {{ item.scheduledRenewal.note }}
        </span>
      </template>
      <template v-slot:[`item.pendingUpdate`]="{ item }">
        {{ item.pendingUpdate }}
        <!-- <pre v-if="item.pendingUpdate.newDueDate">{{
          timestampConverter(item.pendingUpdate.newDueDate)
        }}</pre> -->

        <!-- <span v-if="item.pendingUpdate.newLabels.length">
          <v-chip
            x-small
            outlined
            v-for="(label, index) in item.pendingUpdate.newLabels"
            :key="index"
            class="mr-1"
          >
            {{ label }}</v-chip
          ></span
        > -->
      </template>
      <template v-slot:[`item.actions`]="{}">
        <v-btn icon @click="$emit('editEntry', item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon @click="$emit('scheduleRenewal', item)">
          <v-icon>mdi-calendar-plus</v-icon>
        </v-btn>
        <v-btn icon @click="$emit('deleteEntry', item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import DeadlineEntryStatusChip from "@/components/member-management/deadlines/DeadlineEntryStatusChip.vue";
export default {
  name: "personnel-records-deadlines-data-table",
  props: {
    hideTitle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Fristeneinträge",
    },
    deadlines: {
      type: Array,
      required: true,
    },
    showNames: {
      type: Boolean,
      default: false,
    },
    showDeadline: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showDocuments: {
      type: Boolean,
      default: false,
    },
    showHistory: {
      type: Boolean,
      default: false,
    },
    allowGrouping: {
      type: Boolean,
      default: false,
    },
    allowGroupingByStatus: {
      type: Boolean,
      default: false,
    },
    allowGroupingByPerson: {
      type: Boolean,
      default: false,
    },
    allowGroupingByDeadline: {
      type: Boolean,
      default: false,
    },
    allowExport: {
      type: Boolean,
      default: false,
    },
  },
  components: { DeadlineEntryStatusChip },
  data() {
    return {
      groupBySelector: { value: null },
      search: "",
    };
  },
  computed: {
    headers() {
      let headers = [
        { text: "Ablaufdatum", value: "dueDate", divider: true },
        { text: "Status", value: "status", divider: true },
        { text: "Labels", value: "labels", divider: true },
        { text: "Bemerkungen", value: "note", divider: true },
        {
          text: "Ausstehende Aktualisierung",
          value: "pendingUpdate",
          divider: true,
        },
        {
          text: "Geplante Verlängerung",
          value: "scheduledRenewal",
          divider: true,
        },
      ];

      if (this.showDocuments) {
        headers.splice(4, 0, {
          text: "Dokumente",
          value: "documents",
          divider: true,
        });
      }
      if (
        !(
          this.showDeadline &&
          this.allowGrouping &&
          this.groupBySelector.value === "groupByDeadline"
        )
      ) {
        headers.unshift({
          text: "Frist",
          value: "deadline.shortTitle",
          divider: true,
        });
      }
      if (this.showNames) {
        headers.unshift({
          text: "Nachname",
          value: "user.lastName",
          divider: true,
        });
        headers.unshift({
          text: "Vorname",
          value: "user.firstName",
          divider: true,
        });
        headers.unshift({
          text: "Personalnummer",
          value: "user.personnelNumber",
          divider: true,
        });
      }
      if (this.showActions) {
        headers.push({
          text: "Aktionen",
          value: "actions",
          sortable: false,
        });
      }
      return headers;
    },
    deadlineEntriesWithStatus() {
      return this.deadlines.map((entry) => {
        const deadlineTimestamp = entry.dueDate;
        if (!deadlineTimestamp || !deadlineTimestamp.toDate) {
          return { ...entry, status: "unknown" };
        }
        const currentDate = new Date();
        const deadlineDate = deadlineTimestamp.toDate();
        const status = deadlineDate < currentDate ? "expired" : "valid";
        return {
          ...entry,
          status,
          groupByDeadline: entry.deadline.id,
          groupByPerson: entry.user.membershipId,
          groupByStatus: status,
        };
      });
    },
  },
  methods: {
    timestampConverter(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
    timestampConverterWithTime(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
  },
};
</script>
